/**
 * The five self-declared readiness facts an application is scored on. Ordered as the
 * qualifications checklist renders them, and the ONE place the key list is written —
 * `qualificationItems` attaches the labels to this array rather than restating the keys,
 * so a sixth fact cannot appear in the UI while the score silently ignores it.
 */
export const QUALIFICATION_KEYS = [
	'hasSmartPhoneWithInternet',
	'isAvailableAllDay',
	'hasPartyAgentExperience',
	'isNativeSpeaker',
	'hasPowerBank',
] as const;

export type QualificationKey = (typeof QUALIFICATION_KEYS)[number];

/** Anything carrying the qualification answers — a summary, a detail DTO, or a raw payload. */
export type QualificationAnswers = Partial<Record<QualificationKey, unknown>>;

/** Both roles score the same five facts; only the per-fact weight differs. */
export const AGENT_POINTS_PER_QUALIFICATION = 7;
export const OBSERVER_POINTS_PER_QUALIFICATION = 5;

/**
 * Derived, never asserted — `35` and `25` were previously literals, so adding a sixth
 * qualification would have left both maxima stale and every percentage wrong.
 */
export const AGENT_SCORE_MAX = QUALIFICATION_KEYS.length * AGENT_POINTS_PER_QUALIFICATION;
export const OBSERVER_SCORE_MAX = QUALIFICATION_KEYS.length * OBSERVER_POINTS_PER_QUALIFICATION;

export const pointsPerQualification = (role = 'agent'): number =>
	role === 'observer' ? OBSERVER_POINTS_PER_QUALIFICATION : AGENT_POINTS_PER_QUALIFICATION;

/**
 * Whether one answer counts.
 *
 * Not `!!value`, deliberately. The backend serializes unset entity fields as the literal
 * string `"null"` and booleans sometimes arrive as `"true"` / `"false"` — both truthy, so
 * a plain truthiness check awards full marks for a declined answer and for a field the
 * applicant never saw. Only an explicit affirmative scores.
 */
export const isAffirmativeQualification = (value: unknown): boolean => {
	if (typeof value === 'boolean') return value;
	if (typeof value === 'number') return value === 1;
	if (typeof value === 'string') return /^(true|yes|y|1)$/i.test(value.trim());
	return false;
};

/** How many of the five facts the applicant answered affirmatively (0-5). */
export const countQualifications = (answers?: QualificationAnswers | null): number =>
	answers ? QUALIFICATION_KEYS.filter((key) => isAffirmativeQualification(answers[key])).length : 0;

/**
 * The qualification score for an application, computed from its own answers.
 *
 * An observer scores 5 points per affirmative answer (25 max); an agent 7 (35 max).
 */
export const calculateQualificationScore = (answers?: QualificationAnswers | null, role = 'agent'): number =>
	countQualifications(answers) * pointsPerQualification(role);

export type ScoreTier = 'high' | 'mid' | 'low';

export const getScorePercentage = (raw: number, role = 'agent'): number => {
	const maxScore = role === 'observer' ? OBSERVER_SCORE_MAX : AGENT_SCORE_MAX;
	return Math.round((Math.min(raw, maxScore) / maxScore) * 100);
};
export const getScoreTier = (raw: number): ScoreTier => {
	const pct = getScorePercentage(raw);
	if (pct >= 70) return 'high';
	if (pct >= 50) return 'mid';
	return 'low';
};

export const getScoreTierLabel = (raw: number): string => {
	const tier = getScoreTier(raw);
	if (tier === 'high') return 'Above Average';
	if (tier === 'mid') return 'Average';
	return 'Below Average';
};

export const getScoreColorClass = (raw: number): 'success' | 'warning' | 'danger' => {
	const tier = getScoreTier(raw);
	if (tier === 'high') return 'success';
	if (tier === 'mid') return 'warning';
	return 'danger';
};

export const getScoreBadgeClass = (raw: number): string => {
	const tier = getScoreTier(raw);
	return `is-${tier}`;
};

export const formatScore = (raw: number): string => `${raw}/${AGENT_SCORE_MAX}`;
